import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ListingHeader } from "./listing/ListingHeader";
import { ListingFooter } from "./listing/ListingFooter";
import { MobileContactBar } from "./listing/MobileContactBar";
import { AgentCard } from "./listing/AgentCard";
import { PhotoLightbox } from "./listing/PhotoLightbox";
import { ShowingRequestDialog } from "./listing/ShowingRequestDialog";
import { AdvertiserInquiryDialog } from "./listing/AdvertiserInquiryDialog";
import { DownloadPhotosDialog } from "./listing/DownloadPhotosDialog";
import { LocalExpertsButton } from "./listing/LocalExpertsButton";
import { useListingData, listingFileUrl } from "./listing/useListingData";
import { useHashScroll } from "./listing/useHashScroll";
import { hoaLineFor, DetailsHero, DetailsTitle, FloorplanSection, VideoSection, TourSection, FactsSection, HighlightsSection, DescriptionSection, LocationSection, PreferredPartnersCard } from "./listing/PropertyWebsiteDetailsSections";
import { PhotosSection } from "./listing/PropertyWebsiteHomeSections";

const protectImage = (event) => {
  if (event.target instanceof Element && event.target.closest("img, video")) event.preventDefault();
};

export default function PropertyWebsiteDetails() {
  const { slug, preview, listing, error } = useListingData();
  const [heroIndex, setHeroIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showingOpen, setShowingOpen] = useState(false);
  const [inquireAd, setInquireAd] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const images = useMemo(() => (listing?.files || []).filter((file) => file.is_image), [listing]);
  const ordered = useMemo(() => {
    if (!images.length) return [];
    const cover = images.find((file) => file.id === listing?.cover_file_id);
    return cover ? [cover, ...images.filter((file) => file.id !== cover.id)] : images;
  }, [images, listing]);
  const sponsorsByCategory = {};
  if (listing?.sponsors_enabled !== false) {
    for (const ad of listing?.sponsors?.local_service_card || []) { (sponsorsByCategory[ad.category] ||= []).push(ad); }
  }
  const fileUrl = (file) => listingFileUrl(listing, slug, preview, file);
  const floorplanUrl = listing?.floorplan_file_id ? fileUrl({ id: listing.floorplan_file_id }) : null;

  useHashScroll(Boolean(listing));

  if (error) return <main data-testid="public-listing-error" className="min-h-screen bg-white grid place-items-center p-8 text-center"><h1 className="text-xl font-serif-display text-[#202833]">{error}</h1></main>;
  if (!listing) return <main data-testid="public-listing-loading" className="min-h-screen grid place-items-center bg-white font-mono text-sm text-[#667085]">Loading property details…</main>;

  const contact = listing.contact;
  const brandLabel = contact?.name || listing.full_address || listing.project_name;
  const hoaLine = hoaLineFor(listing);
  const sidebarAds = listing.sponsors_enabled !== false ? (listing.sponsors?.sidebar_card || []).slice(0, 3) : [];
  const hero = ordered[heroIndex];

  return (
    <main data-testid="public-property-details" onContextMenuCapture={protectImage} onDragStartCapture={protectImage} className="min-h-screen bg-white text-[#202833]">
      {preview && <div data-testid="public-listing-preview-banner" className="bg-[#C7A361] text-[#111820] text-center text-xs font-mono uppercase tracking-[0.14em] py-2">Admin preview — this is how the page will look to visitors</div>}
      <ListingHeader slug={slug} brandLabel={brandLabel} preview={preview} onRequestShowing={() => setShowingOpen(true)} onMenuOpenChange={setMobileMenuOpen} />

      <DetailsHero listing={listing} ordered={ordered} hero={hero} heroIndex={heroIndex} setHeroIndex={setHeroIndex} fileUrl={fileUrl} onViewPhoto={setLightboxIndex} />

      <div className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 py-10 sm:py-14 space-y-10">
        <DetailsTitle listing={listing} onDownloadAll={() => setDownloadOpen(true)} />

        <PhotosSection ordered={ordered} fileUrl={fileUrl} onViewPhoto={setLightboxIndex} />

        <section className="grid lg:grid-cols-[1fr_320px] gap-10">
          <div className="space-y-10 min-w-0">
            <FactsSection listing={listing} hoaLine={hoaLine} />
            <HighlightsSection highlights={listing.highlights} />
            <FloorplanSection floorplanUrl={floorplanUrl} />
            <VideoSection video={listing.video} fileUrl={fileUrl} />
            <TourSection tourUrl={listing.tour_url} />
            <LocationSection listing={listing} />
            <DescriptionSection description={listing.website_description} />
          </div>
          <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <AgentCard contact={contact} onRequestShowing={() => setShowingOpen(true)} />
            <PreferredPartnersCard sponsors={sidebarAds} onInquire={setInquireAd} />
          </div>
        </section>
      </div>

      <ListingFooter slug={slug} brandLabel={brandLabel} brokerage={contact?.company} preview={preview} />
      <MobileContactBar phone={contact?.phone} onRequestShowing={() => setShowingOpen(true)} hidden={lightboxIndex !== null || showingOpen || Boolean(inquireAd) || mobileMenuOpen} />
      <LocalExpertsButton slug={slug} sponsorsByCategory={sponsorsByCategory} />

      <AnimatePresence>
        {lightboxIndex !== null && (
          <PhotoLightbox photos={ordered.map((f) => ({ id: f.id, url: fileUrl(f) }))} index={lightboxIndex} onIndexChange={setLightboxIndex} onClose={() => setLightboxIndex(null)} />
        )}
      </AnimatePresence>
      <ShowingRequestDialog open={showingOpen} onClose={() => setShowingOpen(false)} slug={slug} agentName={contact?.name} address={listing.full_address || listing.project_name} />
      <AdvertiserInquiryDialog advertiser={inquireAd} slug={slug} onClose={() => setInquireAd(null)} />
      <DownloadPhotosDialog open={downloadOpen} onClose={() => setDownloadOpen(false)} slug={slug} />
    </main>
  );
}
